import { dashboard, payments, properties as propertiesApi, expenses } from '../api';
import { createPageHeader } from '../components/form';
import { createStatCard } from '../components/card';
import { createDataTable, type Column } from '../components/table';
import { navigate } from '../router';
import type { ChartData, Property, Payment, PaymentTimeline, PropertyExpense } from '../types';

declare const Chart: any;

function fmt(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}
function fmtD(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(n);
}

export async function financialsPage(_params?: Record<string, string>, query?: URLSearchParams): Promise<HTMLElement> {
  const page = document.createElement('div');
  page.appendChild(createPageHeader('Financials'));

  // Controls bar
  const controls = document.createElement('div');
  controls.className = 'flex flex-wrap items-center gap-4 mb-6';

  let propList: Property[] = [];
  try { propList = await propertiesApi.list(); } catch { /* ignore */ }

  controls.innerHTML = `
    <div>
      <label class="text-sm text-gray-500">Property:</label>
      <select id="fin-property" class="input-field w-auto py-1 text-sm ml-1">
        <option value="">All Properties</option>
        ${propList.map(p => `<option value="${p.id}">${p.address}, ${p.suburb}</option>`).join('')}
      </select>
    </div>
    <div>
      <label class="text-sm text-gray-500">Period:</label>
      <select id="fin-period" class="input-field w-auto py-1 text-sm ml-1">
        <option value="3">Last 3 Months</option>
        <option value="6" selected>Last 6 Months</option>
        <option value="12">Last 12 Months</option>
        <option value="24">Last 2 Years</option>
        <option value="custom">Custom Range</option>
      </select>
    </div>
    <div id="fin-custom-range" class="hidden flex items-center gap-2">
      <label class="text-sm text-gray-500">From:</label>
      <input type="month" id="fin-from" class="input-field w-auto py-1 text-sm" />
      <label class="text-sm text-gray-500">To:</label>
      <input type="month" id="fin-to" class="input-field w-auto py-1 text-sm" />
      <button id="fin-apply-range" class="btn-primary text-sm py-1 px-3">Apply</button>
    </div>
    <div>
      <label class="text-sm text-gray-500">View:</label>
      <select id="fin-direction" class="input-field w-auto py-1 text-sm ml-1">
        <option value="all">All</option>
        <option value="incoming">Income Only</option>
        <option value="outgoing">Expenses Only</option>
      </select>
    </div>
    <button id="fin-pl-toggle" class="btn-outline text-sm py-1 px-3">By Property (P&amp;L)</button>
  `;
  page.appendChild(controls);

  const content = document.createElement('div');
  page.appendChild(content);

  let chartData: ChartData | null = null;
  let timelineData: PaymentTimeline[] | null = null;
  let allPayments: Map<string, Payment[]> = new Map();
  let allExpenses: Map<string, PropertyExpense[]> = new Map();
  let selectedProp = '';
  let monthsBack = 6;
  let direction = 'all';
  let filteredMonth: string | null = query?.get('month') || null;
  let plView = false;  // Property P&L view toggle

  const propMap = new Map<string, Property>();
  propList.forEach(p => propMap.set(p.id, p));

  async function loadData() {
    content.innerHTML = '<div class="text-center py-8 text-gray-400">Loading financial data...</div>';
    try {
      const [cd, tl] = await Promise.all([
        dashboard.chart(monthsBack, 3, selectedProp || undefined),
        payments.timeline(monthsBack),
      ]);
      chartData = cd;
      timelineData = tl;

      // Load payments for all (or selected) properties
      allPayments.clear();
      allExpenses.clear();
      const propsToLoad = selectedProp ? [selectedProp] : propList.map(p => p.id);
      const [paymentResults, expenseResults] = await Promise.all([
        Promise.all(propsToLoad.map(pid => payments.list(pid).then(p => ({ pid, p })).catch(() => ({ pid, p: [] as Payment[] })))),
        Promise.all(propsToLoad.map(pid => expenses.list(pid).then(e => ({ pid, e })).catch(() => ({ pid, e: [] as PropertyExpense[] })))),
      ]);
      for (const { pid, p } of paymentResults) allPayments.set(pid, p);
      for (const { pid, e } of expenseResults) allExpenses.set(pid, e);
    } catch {
      content.innerHTML = '<div class="text-red-500 p-4">Failed to load financial data.</div>';
      return;
    }
    render();
  }

  function render() {
    if (!chartData || !timelineData) return;
    content.innerHTML = '';

    // P&L view (F2)
    if (plView) {
      renderPLTable();
      return;
    }

    // Summary stats
    const totalIncome = chartData.rent_income.reduce((a, b) => a + b, 0);
    const totalFees = chartData.agent_fees.reduce((a, b) => a + b, 0);
    const totalMaint = chartData.maintenance_costs.reduce((a, b) => a + b, 0);
    const totalFixed = chartData.fixed_expenses_monthly * chartData.months.length;
    const totalNet = totalIncome - totalFees - totalMaint - totalFixed;

    if (direction !== 'outgoing') {
      const statsRow = document.createElement('div');
      statsRow.className = 'grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6';
      statsRow.appendChild(createStatCard('Total Income', fmt(totalIncome), `${monthsBack} months`, 'text-green-600'));
      statsRow.appendChild(createStatCard('Agent Fees', fmt(totalFees), undefined, 'text-red-500'));
      statsRow.appendChild(createStatCard('Maintenance', fmt(totalMaint), undefined, 'text-orange-500'));
      statsRow.appendChild(createStatCard('Fixed Costs', fmt(totalFixed), undefined, 'text-purple-500'));
      statsRow.appendChild(createStatCard('Net Income', fmt(totalNet), undefined, totalNet >= 0 ? 'text-green-600' : 'text-red-500'));
      content.appendChild(statsRow);
    }

    // Chart.js stacked bar chart
    const chartCard = document.createElement('div');
    chartCard.className = 'card mb-6';
    chartCard.innerHTML = '<h3 class="font-semibold text-gray-900 mb-4">Income vs Expenses by Month</h3>';
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.height = '300px';
    const canvas = document.createElement('canvas');
    canvas.id = 'financials-chart';
    wrapper.appendChild(canvas);
    chartCard.appendChild(wrapper);
    content.appendChild(chartCard);

    // Render chart after element is in DOM
    setTimeout(() => renderChart(canvas, chartData!), 0);

    // Expected vs Received timeline
    if (timelineData && timelineData.length > 0 && direction !== 'outgoing') {
      const tlCard = document.createElement('div');
      tlCard.className = 'card mb-6';
      tlCard.innerHTML = '<h3 class="font-semibold text-gray-900 mb-4">Expected vs Received by Property</h3>';

      const filtered = selectedProp
        ? timelineData.filter(t => t.property_id === selectedProp)
        : timelineData;

      if (filtered.length > 0) {
        const months = filtered[0].months.map(m => m.month);
        const tableDiv = document.createElement('div');
        tableDiv.className = 'overflow-x-auto';
        tableDiv.innerHTML = `
          <table class="data-table text-sm">
            <thead><tr>
              <th>Property</th>
              <th class="text-right">Rent/wk</th>
              ${months.map(m => `<th class="text-center">${m.substring(5)}</th>`).join('')}
            </tr></thead>
            <tbody>
              ${filtered.map(t => `
                <tr class="cursor-pointer hover:bg-gray-50" data-pid="${t.property_id}">
                  <td class="font-medium text-primary-600">${t.address}</td>
                  <td class="text-right">${fmt(t.weekly_rent)}</td>
                  ${t.months.map(m => {
                    const c = m.amount_color || m.color;
                    const bg = c === 'green' ? 'bg-green-50 text-green-700' :
                      c === 'amber' ? 'bg-amber-50 text-amber-700' :
                      c === 'red' ? 'bg-red-50 text-red-700' : '';
                    return `<td class="text-center ${bg}"><div class="text-sm font-medium">${fmt(m.actual)}</div><div class="text-xs text-gray-400">${fmt(m.expected)}</div></td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
        tlCard.appendChild(tableDiv);

        // Navigate on row click
        setTimeout(() => {
          tableDiv.querySelectorAll('tr[data-pid]').forEach(tr => {
            tr.addEventListener('click', () => navigate(`#/properties/${(tr as HTMLElement).dataset.pid}`));
          });
        });
      }
      content.appendChild(tlCard);
    }

    // Missed / Partial payments section (F1)
    if (direction !== 'outgoing' && timelineData && timelineData.length > 0) {
      renderMissedPayments();
    }

    // Payment details table (with filter support F3)
    renderPaymentTable();

    // Expense details table
    if (direction !== 'incoming') {
      renderExpenseTable();
    }
  }

  function renderChart(canvas: HTMLCanvasElement, cd: ChartData) {
    // Split into past/current and projected future months (F4)
    const curMonth = cd.current_month || cd.months[cd.months.length - 1] || '';
    const idx = (m: string) => cd.months.indexOf(m);
    const pastMonths = cd.months.filter(m => m <= curMonth);
    const futureMonths = cd.months.filter(m => m > curMonth);
    const displayMonths = [...pastMonths, ...futureMonths];
    const labels = displayMonths.map(m => m > curMonth ? `${m.substring(5)}*` : m.substring(5));

    const rentData = displayMonths.map(m => {
      const i = idx(m);
      return m > curMonth ? cd.expected_monthly_rent : (i >= 0 ? cd.rent_income[i] : 0);
    });
    const feesData = displayMonths.map(m => {
      const i = idx(m);
      return m > curMonth ? cd.projected_monthly_fees : (i >= 0 ? cd.agent_fees[i] : 0);
    });
    const maintData = displayMonths.map(m => {
      const i = idx(m);
      return m > curMonth ? 0 : (i >= 0 ? cd.maintenance_costs[i] : 0);
    });
    const fixedData = displayMonths.map(() => cd.fixed_expenses_monthly);
    const netData = displayMonths.map((_, j) => rentData[j] - feesData[j] - maintData[j] - fixedData[j]);

    const datasets: any[] = [];
    if (direction !== 'outgoing') {
      datasets.push({
        label: 'Rent Income',
        data: rentData,
        backgroundColor: displayMonths.map(m => m > curMonth ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.7)'),
        stack: 'income',
      });
    }
    if (direction !== 'incoming') {
      datasets.push({
        label: 'Agent Fees',
        data: feesData,
        backgroundColor: displayMonths.map(m => m > curMonth ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.7)'),
        stack: 'expenses',
      });
      datasets.push({
        label: 'Maintenance',
        data: maintData,
        backgroundColor: 'rgba(249, 115, 22, 0.7)',
        stack: 'expenses',
      });
      datasets.push({
        label: 'Fixed Costs',
        data: fixedData,
        backgroundColor: displayMonths.map(m => m > curMonth ? 'rgba(168,85,247,0.3)' : 'rgba(168,85,247,0.7)'),
        stack: 'expenses',
      });
    }
    if (direction === 'all') {
      datasets.push({
        label: 'Net Income',
        data: netData,
        type: 'line',
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        pointRadius: 4,
        fill: false,
        tension: 0.3,
      });
    }

    new Chart(canvas, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          tooltip: {
            position: 'nearest',
            backgroundColor: '#21262d',
            titleColor: '#e6edf3',
            bodyColor: '#c9d1d9',
            borderColor: '#30363d',
            borderWidth: 1,
            callbacks: {
              label: (ctx: any) => `${ctx.dataset.label}: ${fmtD(ctx.parsed.y)}`,
            },
          },
          legend: {
            labels: { color: '#8b949e' },
          },
        },
        scales: {
          x: { stacked: true, ticks: { color: '#8b949e' }, grid: { color: '#21262d' } },
          y: { stacked: true, ticks: { callback: (v: number) => fmt(v), color: '#8b949e' }, grid: { color: '#21262d' } },
        },
        onClick: (_evt: any, elements: any[]) => {
          if (elements.length > 0) {
            const clickedMonth = displayMonths[elements[0].index];
            if (clickedMonth && clickedMonth <= curMonth) {
              showMonthDetail(clickedMonth);
            }
          }
        },
      },
    });
  }

  // F3: Filter payment table to selected month
  function showMonthDetail(month: string) {
    filteredMonth = filteredMonth === month ? null : month;
    renderPaymentTable();
    if (filteredMonth) {
      const tableEl = content.querySelector('#payment-table-card');
      if (tableEl) tableEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function renderPaymentTable() {
    if (direction === 'outgoing') return;

    // Remove existing payment table card and re-render
    content.querySelector('#payment-table-card')?.remove();

    const card = document.createElement('div');
    card.id = 'payment-table-card';
    card.className = 'card mb-6';

    // Filter label (F3)
    const header = document.createElement('div');
    header.className = 'flex items-center gap-3 mb-4';
    header.innerHTML = '<h3 class="font-semibold text-gray-900">Payment Records</h3>';
    if (filteredMonth) {
      const clearBtn = document.createElement('button');
      clearBtn.className = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium hover:bg-blue-200';
      clearBtn.innerHTML = `Filtered: ${filteredMonth} &times;`;
      clearBtn.addEventListener('click', () => { filteredMonth = null; renderPaymentTable(); });
      header.appendChild(clearBtn);
    }
    card.appendChild(header);

    type PaymentRow = Payment & { property_address: string };
    let rows: PaymentRow[] = [];
    for (const [pid, payList] of allPayments) {
      const prop = propMap.get(pid);
      const addr = prop ? `${prop.address}, ${prop.suburb}` : pid;
      for (const p of payList) {
        if (p.amount > 0) rows.push({ ...p, property_address: addr });
      }
    }
    rows.sort((a, b) => (b.payment_date || '').localeCompare(a.payment_date || ''));

    // Apply month filter
    if (filteredMonth) {
      rows = rows.filter(r => r.payment_date?.startsWith(filteredMonth!));
    }

    const cols: Column<PaymentRow>[] = [
      { label: 'Date', key: 'payment_date' },
      { label: 'Property', render: r => `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.property_address}</a>` },
      { label: 'Amount', render: r => fmtD(r.amount), className: 'text-right' },
      { label: 'Type', render: r => `<span class="badge badge-${r.type === 'rent' ? 'green' : 'blue'}">${r.type}</span>` },
      { label: 'Source', render: r => `<span class="badge badge-gray">${r.source}</span>` },
      { label: 'Reference', key: 'reference', className: 'max-w-xs truncate' },
    ];

    const table = createDataTable(cols, rows, {
      emptyText: filteredMonth ? `No payments in ${filteredMonth}.` : 'No payment records.',
      onRowClick: (row) => navigate(`#/properties/${row.property_id}`),
    });

    const tbody = table.querySelector('tbody');
    if (tbody) {
      tbody.querySelectorAll('tr').forEach((tr, i) => {
        if (rows[i]) {
          tr.classList.add('payment-row');
          tr.dataset.month = rows[i].payment_date?.substring(0, 7) || '';
        }
      });
    }

    card.appendChild(table);
    content.appendChild(card);
  }

  // F1: Missed / Partial Payments table
  function renderMissedPayments() {
    if (!timelineData) return;
    const today = new Date().toISOString().substring(0, 7);

    type MissRow = { property: string; property_id: string; month: string; expected: number; actual: number; shortfall: number; status: string };
    const rows: MissRow[] = [];

    for (const tl of timelineData) {
      const filtered = selectedProp ? tl.property_id === selectedProp : true;
      if (!filtered) continue;
      for (const m of tl.months) {
        if (m.month > today) continue;  // exclude future months
        if (m.expected <= 0) continue;
        const shortfall = m.expected - m.actual;
        if (shortfall > m.expected * 0.05) {
          rows.push({
            property: tl.address,
            property_id: tl.property_id,
            month: m.month,
            expected: m.expected,
            actual: m.actual,
            shortfall,
            status: m.color === 'red' ? 'Missed' : 'Partial',
          });
        }
      }
    }

    if (rows.length === 0) return;

    rows.sort((a, b) => b.month.localeCompare(a.month));

    const card = document.createElement('div');
    card.className = 'card mb-6';
    card.innerHTML = `<h3 class="font-semibold text-gray-900 mb-4">Missed / Partial Payments <span class="text-sm font-normal text-red-500">${rows.length} issue${rows.length === 1 ? '' : 's'}</span></h3>`;

    const cols: Column<MissRow>[] = [
      { label: 'Property', render: r => `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.property}</a>` },
      { label: 'Month', key: 'month' },
      { label: 'Expected', render: r => fmtD(r.expected), className: 'text-right' },
      { label: 'Received', render: r => fmtD(r.actual), className: 'text-right' },
      { label: 'Shortfall', render: r => `<span class="text-red-600 font-medium">${fmtD(r.shortfall)}</span>`, className: 'text-right' },
      { label: 'Status', render: r => `<span class="badge ${r.status === 'Missed' ? 'badge-red' : 'badge-yellow'}">${r.status}</span>` },
    ];

    card.appendChild(createDataTable(cols, rows, { emptyText: '' }));
    content.appendChild(card);
  }

  function renderExpenseTable() {
    const card = document.createElement('div');
    card.className = 'card mb-6';
    card.innerHTML = '<h3 class="font-semibold text-gray-900 mb-4">Recorded Expenses</h3>';

    type ExpenseRow = PropertyExpense & { property_address: string };
    const rows: ExpenseRow[] = [];
    for (const [pid, expList] of allExpenses) {
      const prop = propMap.get(pid);
      const addr = prop ? `${prop.address}, ${prop.suburb}` : pid;
      for (const e of expList) {
        rows.push({ ...e, property_address: addr });
      }
    }
    rows.sort((a, b) => b.annual_amount - a.annual_amount);

    const cols: Column<ExpenseRow>[] = [
      { label: 'Property', render: r => `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.property_address}</a>` },
      { label: 'Type', render: r => `<span class="badge badge-blue">${r.type}</span>` },
      { label: 'Label', key: 'label' },
      { label: 'Annual', render: r => fmtD(r.annual_amount), className: 'text-right' },
      { label: 'Monthly', render: r => fmtD(r.annual_amount / 12), className: 'text-right' },
      { label: 'Period', key: 'period' },
    ];
    card.appendChild(createDataTable(cols, rows, { emptyText: 'No expenses recorded.' }));
    content.appendChild(card);
  }

  // F2: Property P&L table
  function renderPLTable() {
    const card = document.createElement('div');
    card.className = 'card mb-6';
    card.innerHTML = '<h3 class="font-semibold text-gray-900 mb-4">Property P&amp;L <span class="text-sm font-normal text-gray-400">— selected period</span></h3>';

    type PLRow = { property_id: string; address: string; total_income: number; agent_fees: number; fixed_costs: number; net: number; estimated_value: number };
    const rows: PLRow[] = [];

    for (const [pid, payList] of allPayments) {
      const prop = propMap.get(pid);
      if (!prop) continue;
      const total_income = payList.reduce((s, p) => s + (p.amount > 0 ? p.amount : 0), 0);
      const agent_fees = total_income * (prop.management_fee_percent / 100);
      const expList = allExpenses.get(pid) || [];
      const fixed_costs = expList.reduce((s, e) => s + e.annual_amount / 12 * monthsBack, 0);
      const net = total_income - agent_fees - fixed_costs;
      rows.push({
        property_id: pid,
        address: `${prop.address}, ${prop.suburb}`,
        total_income,
        agent_fees,
        fixed_costs,
        net,
        estimated_value: prop.estimated_value || 0,
      });
    }
    rows.sort((a, b) => b.net - a.net);

    type PLRowCol = PLRow;
    const cols: Column<PLRowCol>[] = [
      { label: 'Property', render: r => `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.address}</a>` },
      { label: 'Total Income', render: r => fmtD(r.total_income), className: 'text-right' },
      { label: 'Agent Fees', render: r => `<span class="text-red-600">${fmtD(r.agent_fees)}</span>`, className: 'text-right' },
      { label: 'Fixed Costs', render: r => `<span class="text-purple-600">${fmtD(r.fixed_costs)}</span>`, className: 'text-right' },
      { label: 'Net', render: r => `<span class="font-semibold ${r.net >= 0 ? 'text-green-600' : 'text-red-500'}">${fmtD(r.net)}</span>`, className: 'text-right' },
      {
        label: 'Yield (annualised)',
        render: r => r.estimated_value > 0
          ? `${((r.net / monthsBack * 12) / r.estimated_value * 100).toFixed(2)}%`
          : '—',
        className: 'text-right',
      },
    ];

    card.appendChild(createDataTable(cols, rows, { emptyText: 'No data available.' }));
    content.appendChild(card);
  }

  // Event handlers
  controls.querySelector('#fin-property')!.addEventListener('change', (e) => {
    selectedProp = (e.target as HTMLSelectElement).value;
    loadData();
  });
  controls.querySelector('#fin-period')!.addEventListener('change', (e) => {
    const val = (e.target as HTMLSelectElement).value;
    const customDiv = controls.querySelector('#fin-custom-range') as HTMLElement;
    if (val === 'custom') {
      customDiv.classList.remove('hidden');
      // Pre-fill with current range
      const now = new Date();
      const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const fromDate = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
      const from = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}`;
      (controls.querySelector('#fin-from') as HTMLInputElement).value = from;
      (controls.querySelector('#fin-to') as HTMLInputElement).value = to;
    } else {
      customDiv.classList.add('hidden');
      monthsBack = parseInt(val);
      loadData();
    }
  });
  controls.querySelector('#fin-apply-range')!.addEventListener('click', () => {
    const from = (controls.querySelector('#fin-from') as HTMLInputElement).value;
    const to = (controls.querySelector('#fin-to') as HTMLInputElement).value;
    if (from && to) {
      const [fy, fm] = from.split('-').map(Number);
      const [ty, tm] = to.split('-').map(Number);
      monthsBack = (ty - fy) * 12 + (tm - fm) + 1;
      if (monthsBack < 1) monthsBack = 1;
      loadData();
    }
  });
  controls.querySelector('#fin-direction')!.addEventListener('change', (e) => {
    direction = (e.target as HTMLSelectElement).value;
    render();
  });
  controls.querySelector('#fin-pl-toggle')!.addEventListener('click', () => {
    plView = !plView;
    const btn = controls.querySelector('#fin-pl-toggle') as HTMLButtonElement;
    btn.classList.toggle('btn-primary', plView);
    btn.classList.toggle('btn-outline', !plView);
    render();
  });

  await loadData();
  return page;
}
